import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api.ts";
import type { CashierOut, TokenResponse, UserOut } from "@/types/api.ts";
import { IS_ONLINE_EDITION } from "@/lib/edition.ts";
import { useAuthStore } from "@/stores/auth.ts";
import { useCompanyStore } from "@/stores/company.ts";

export const authKeys = {
  all: ["auth"] as const,
  cashiers: (ruc: string | null = null) => [...authKeys.all, "cashiers", ruc] as const,
  me: () => [...authKeys.all, "me"] as const,
};

export function useCashiers() {
  // En la edición online la lista depende de la empresa elegida.
  const ruc = useCompanyStore((s) => s.ruc);
  return useQuery({
    queryKey: authKeys.cashiers(ruc),
    queryFn: () => api.get<CashierOut[]>("/auth/cashiers"),
    enabled: !IS_ONLINE_EDITION || ruc !== null,
    staleTime: 60_000,
  });
}

interface LoginPinVars {
  user_id: string;
  pin: string;
}

export function useLoginPin() {
  const queryClient = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);

  return useMutation({
    mutationFn: async (vars: LoginPinVars) => {
      const tokenResp = await api.post<TokenResponse>("/auth/login-pin", vars);
      const me = await api.get<UserOut>("/auth/me", {
        headers: { Authorization: `Bearer ${tokenResp.access_token}` },
      });
      setSession(tokenResp.access_token, me);
      queryClient.setQueryData(authKeys.me(), me);
      return { token: tokenResp.access_token, user: me };
    },
  });
}

interface LoginPasswordVars {
  username: string;
  password: string;
}

export function useLoginPassword() {
  const queryClient = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);

  return useMutation({
    mutationFn: async (vars: LoginPasswordVars) => {
      const tokenResp = await api.post<TokenResponse>("/auth/login", vars);
      const me = await api.get<UserOut>("/auth/me", {
        headers: { Authorization: `Bearer ${tokenResp.access_token}` },
      });
      setSession(tokenResp.access_token, me);
      queryClient.setQueryData(authKeys.me(), me);
      return { token: tokenResp.access_token, user: me };
    },
  });
}
